import { useEffect, useState } from 'react'
import { Button, Dropdown, Input, Tag } from 'components'
import Flex from 'components/priority/Flex/Flex'
import Text from 'components/priority/Text/Text'
import Icon from 'components/priority/Icon/Icon'
import { Tooltip } from '@/components/global/Tooltip/Tooltip'
import { editorPagesAtom, isSavingAtom } from '@/atoms/_editorAtoms'
import {
  editorLayoutAtom,
  editorOrderPageSetAtom,
  triggerUseEffect,
  themeIdAtom,
  orderIdAtom,
  applicationTypeAtom,
} from 'atoms'
import { useIsAdmin } from '@/hooks/utility/useIsAdmin'
import { pageSetLocaleAtom } from '@/atoms/_editorAtoms'
import { useRecoilValue, useSetRecoilState, useRecoilState } from 'recoil'
import { useSetToast } from 'hooks'
import { default as _omit } from 'lodash/omit'
import { ToggleSwitch } from 'components/global/ToggleSwitch/ToggleSwitch'
import { useErrorHandler } from 'hooks/utility/useErrorHandler'
import { templatePageBodies } from '@/utils/editor/templatePageBodies'
import { useEditorLayoutSaveRefusal } from '@/hooks/editor/useEditorLayoutSaveRefusal'
import { logger, EventCategory } from 'utils/logger'
import { useRecoilLogData } from 'utils/recoilLogger'
import {
  LOCALE,
  LOCALE_OPTIONS,
  LOTUS_FUNERAL_HOME_NOT_ENABLED_CODE,
} from '@messenger/consts'
import { getLanguageDisplayName } from '@/utils/global/getLanguageDisplayName'
import { useTranslation } from 'react-i18next'
import { useDropEditorTextBoxWork } from '@/hooks/editor/useDropEditorTextBoxWork'
import { useEditorSaveTextBoxes } from '@/hooks/editor/useEditorSaveTextBoxes'
import { useEditorSaveGesture } from '@/hooks/editor/useEditorSaveGesture'
import { archive, getCustomPageSets, replacePages } from '@/api/pageSets'
import { getPages } from '@/api/pages'
import { applyOrderPageSetLayout } from '@/api/orderPageSets'

/**
 * The body that creates one order page from a page of the layout being
 * applied: the layout page's fields and its whole page JSON, placed in the
 * order's page set and naming the layout page its text boxes copy from.
 */
export const layoutPageToOrderPage = (page, orderPageSetId) => {
  const newPage = _omit(page, ['archived', 'id', 'json_template'])
  newPage.order_page_set_id = orderPageSetId
  newPage.json_template = JSON.stringify(page.json_template)
  newPage.source_page_id = page.id
  return newPage
}

export const SelectLayoutDrawer = ({ setShow, basePages, setIsLoading }) => {
  const { t } = useTranslation()
  const recoilLogData = useRecoilLogData()
  const handleError = useErrorHandler('SelectLayoutDrawer')
  const saveEditorTextBoxes = useEditorSaveTextBoxes()
  const runSaveGesture = useEditorSaveGesture()
  const dropTextBoxWorkForPages = useDropEditorTextBoxWork()
  const isSaving = useRecoilValue(isSavingAtom)
  const layoutId = useRecoilValue(editorLayoutAtom)
  const editorPages = useRecoilValue(editorPagesAtom)
  const [orderPageSet, setOrderPageSet] = useRecoilState(editorOrderPageSetAtom)
  const [layouts, setLayouts] = useState([])
  const [selectedLayoutId, setSelectedLayoutId] = useState(null)
  const [selectedLayout, setSelectedLayout] = useState(null)
  const [layoutIsDefault, setLayoutIsDefault] = useState(null)
  const [layoutName, setLayoutName] = useState(null)
  const [currentLayout, setCurrentLayout] = useState(null)
  const themeId = useRecoilValue(themeIdAtom)
  const setToast = useSetToast()
  const setTrigger = useSetRecoilState(triggerUseEffect)
  const [isGlobalTemplate, setIsGlobalTemplate] = useState(null)
  const [layoutLanguage, setLayoutLanguage] = useState(LOCALE.EN)
  const orderId = useRecoilValue(orderIdAtom)
  const setPageSetLocale = useSetRecoilState(pageSetLocaleAtom)
  const isAdmin = useIsAdmin()
  const applicationType = useRecoilValue(applicationTypeAtom)
  const showSaveRefusal = useEditorLayoutSaveRefusal(
    'SelectLayoutDrawer',
    setShow,
  )

  const getSets = async () => {
    try {
      const { data } = await getCustomPageSets(
        layoutId,
        themeId,
        orderId,
        applicationType,
      )

      // Defaults first, then by name, with global and funeral-home layouts
      // mixed.
      const byDefaultThenName = (a, b) =>
        Number(Boolean(b.default)) - Number(Boolean(a.default)) ||
        String(a.name ?? '').localeCompare(String(b.name ?? ''))

      setLayouts([...data].sort(byDefaultThenName))
    } catch (error) {
      handleError(error?.response?.data?.message || error.message, error)
    }
  }

  const archiveLayout = async () => {
    try {
      await archive(selectedLayout.id)

      setToast({
        text: t('toast.layoutDeletedSuccess'),
      })
      getSets()
    } catch (error) {
      handleError(error?.response?.data?.message || error.message, error)
    }
  }

  /**
   * Writes the layout's pages and fields in one request, which changes the
   * whole template or nothing, and says why when it is refused. Answers
   * whether it was written.
   */
  const updateTemplate = async () => {
    try {
      const { data } = await replacePages(selectedLayout.id, {
        name: layoutName,
        default: layoutIsDefault,
        language: layoutLanguage,
        homeTemplate: !isGlobalTemplate,
        pages: templatePageBodies(basePages, editorPages),
      })
      getSets()
      return data.template
    } catch (error) {
      if (showSaveRefusal(error)) getSets()
      return null
    }
  }

  const updateLayout = async () => {
    try {
      // The layout is a copy of the page rows, so the unsaved text-box edits
      // land first. That write is a Save of the line, so it runs as one:
      // under the tab's save latch, after a version of the line. The template
      // update below writes a template rather than the line, so it runs
      // outside the latch.
      const savedBoxes = await runSaveGesture(
        'update_layout',
        applicationType,
        () => saveEditorTextBoxes(),
      )
      // Another Save is writing the line; its own gesture says so.
      if (savedBoxes === undefined) return
      if (savedBoxes.stale) {
        // The page this layout would copy is no longer on the line, so the
        // copy has nothing to take. Its drafts go with it, and the message
        // names what happened instead of blaming the save.
        dropTextBoxWorkForPages(savedBoxes.stalePageIds)
        setToast({ text: t('toast.editorPagesReplaced') })
        return
      }
      // This tab's link no longer reaches the order; the editor's access-lost
      // prompt already says the save did not complete.
      if (savedBoxes.accessLost) return
      if (!savedBoxes.saved) {
        setToast({ text: t('toast.layoutTextBoxSaveFailed') })
        return
      }
      const template = await updateTemplate()
      // A refused update has shown its own message.
      if (template === null) return

      setToast({
        text:
          template.type === 'GLOBAL'
            ? t('toast.globalTemplateUpdatedSuccess')
            : t('toast.layoutUpdatedSuccess'),
      })
    } catch (error) {
      handleError(error?.response?.data?.message || error.message, error)
    }
  }

  // One request replaces the line's pages with the layout's, or changes
  // nothing: a refusal or a failure leaves the line as it was.
  const applyLayout = async () => {
    setIsLoading(true)
    // Whether the apply itself was sent: a request that got no answer may
    // have been written, while a failed read of the layout wrote nothing.
    let applySent = false
    try {
      const { data: layoutPages } = await getPages(selectedLayout.id)
      applySent = true
      const { data } = await applyOrderPageSetLayout(
        orderPageSet.id,
        {
          template_id: selectedLayout.id,
          template_type: selectedLayout.type,
          pages: layoutPages.map((page) =>
            layoutPageToOrderPage(page, orderPageSet.id),
          ),
        },
        applicationType,
      )
      const newPageSetData = data.page_set
      const newPages = data.pages

      setCurrentLayout(newPageSetData)
      setOrderPageSet(newPageSetData)
      setSelectedLayoutId(newPageSetData.id)

      setPageSetLocale(newPageSetData?.language || LOCALE.EN)

      setIsLoading(false)
      setShow(false)
      setTrigger(Date.now())

      logger.info({
        message: 'editor_saved_layout_applied_successfully',
        category: EventCategory.EDIT,
        data: {
          ...recoilLogData,
          newPageSetId: newPageSetData.id,
          newPagesCount: newPages.length,
        },
      })
    } catch (error) {
      logger.error({
        message: 'editor_saved_layout_apply_failed',
        category: EventCategory.ERROR,
        error: error,
        data: {
          ...recoilLogData,
          selectedLayoutId: selectedLayout.id,
          orderPageSetId: orderPageSet.id,
        },
      })
      // A gateway's 502, 503 or 504 says nothing about whether the backend
      // wrote the apply, just as a missing answer does not.
      const unanswered =
        error?.response === undefined ||
        error?.code === 'ECONNABORTED' ||
        [502, 503, 504].includes(error?.response?.status)
      setToast({
        text:
          applySent && unanswered
            ? t('toast.layoutApplyUnconfirmed')
            : error?.response?.data?.code ===
                LOTUS_FUNERAL_HOME_NOT_ENABLED_CODE
              ? t('toast.layoutApplyLotusRefused')
              : t('toast.layoutApplyNothingChanged'),
      })
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    getSets()
    return () => {
      setIsLoading(false)
    }
    //eslint-disable-next-line
  }, [])

  useEffect(() => {
    try {
      const current = layouts.find(
        (item) => item.id === orderPageSet.page_set_id,
      )
      setCurrentLayout(current)
      if (current) {
        setSelectedLayoutId(current.page_set_id)
      }
    } catch (error) {
      handleError(error?.response?.data?.message || error.message, error)
    }
    //eslint-disable-next-line
  }, [layouts, orderPageSet])

  useEffect(() => {
    try {
      const layout = layouts.find((item) => item.id === selectedLayoutId)
      if (layout) {
        setLayoutIsDefault(layout?.default || false)
        if (layout?.name) setLayoutName(layout.name)
        if (layout?.language) setLayoutLanguage(layout.language || LOCALE.EN)
        setSelectedLayout(layout)
        setIsGlobalTemplate(layout?.type === 'GLOBAL')
      }
    } catch (error) {
      handleError(error?.response?.data?.message || error.message, error)
    }
    //eslint-disable-next-line
  }, [selectedLayoutId, layouts])

  // Rename, default, delete and Update layout are internal staff's: the
  // list carries no field that would offer them to a funeral home user.
  const showSavedLayoutControls = isAdmin
  const isGlobalLayout = selectedLayout?.type === 'GLOBAL'

  const getLanguageLabel = (language) => {
    const displayName = getLanguageDisplayName(language)
    return t(`editor.language.${displayName}`)
  }

  return (
    <Flex margin="16px 24px" column>
      <Flex margin="16px 0px">
        <Text size="16px">
          {t('editor.menus.assets.savedLayouts.currentLayout')}
        </Text>
        <Text size="16px" weight="600">
          {currentLayout?.name}
        </Text>
      </Flex>
      <Flex margin="16px 0px">
        {currentLayout?.language && (
          <>
            <Text size="16px">
              {t('editor.menus.assets.savedLayouts.currentLayoutLanguage')}
            </Text>
            <Tag>{getLanguageLabel(currentLayout.language)}</Tag>
          </>
        )}
      </Flex>
      <Flex column>
        <Text>{t('editor.menus.assets.savedLayouts.yourSavedLayouts')}</Text>
        <Dropdown
          selected={[selectedLayoutId, setSelectedLayoutId]}
          placeholder={t('editor.menus.assets.savedLayouts.placeholder')}
          textKey="name"
          customLayout
          valueKey="id"
          items={layouts}
          margin="16px"
        />
      </Flex>

      {selectedLayout && (
        <Flex column background="gray5" padding="16px">
          <Flex justify="space-between" align="center" margin="16px 0">
            <Flex column gap="6px">
              <Text>
                {t('editor.menus.assets.savedLayouts.selectedLayout')}
                <Text size="18" weight="600">
                  {selectedLayout.name}
                </Text>
              </Text>
              {isGlobalLayout && (
                <Flex justify="start">
                  <Tag>
                    {t('editor.menus.assets.savedLayouts.globalTemplate')}
                  </Tag>
                </Flex>
              )}
            </Flex>
            <Button
              primary
              disabled={isSaving}
              onClick={applyLayout}
              data-testid="saved-layout-apply-button"
            >
              {t('editor.menus.assets.savedLayouts.applyToProduct')}
            </Button>
          </Flex>

          {showSavedLayoutControls && (
            <Flex column gap="12px">
              <Flex
                justify="space-between"
                align="center"
                padding="16px 0 8px"
                borderTop="1px solid #B0B6BC"
              >
                <Flex justify="space-between">
                  <Tooltip
                    text={t(
                      'editor.menus.assets.savedLayouts.changeLayoutName',
                    )}
                    margin="0 12px 0 0"
                  >
                    <Input
                      label={t('editor.menus.assets.savedLayouts.layoutName')}
                      placeholder={t(
                        'editor.menus.assets.savedLayouts.layoutNamePlaceholder',
                      )}
                      value={[layoutName, setLayoutName]}
                      width="200px"
                      disabled={!showSavedLayoutControls}
                    />
                  </Tooltip>
                  <Tooltip
                    text={t(
                      'editor.menus.assets.savedLayouts.makeLayoutDefault',
                    )}
                    margin="0 12px 0 0"
                  >
                    <ToggleSwitch
                      label={t('editor.menus.assets.savedLayouts.defaultLabel')}
                      value={[layoutIsDefault, setLayoutIsDefault]}
                      margin="0 16px"
                      disabled={!showSavedLayoutControls}
                    />
                  </Tooltip>
                </Flex>
                <Flex justify="flex-end">
                  <Tooltip
                    text={t('editor.menus.assets.savedLayouts.deleteLayout')}
                    margin="0 12px 0 0"
                  >
                    <Icon
                      height="16px"
                      width="16px"
                      margin="8px"
                      icon="archive"
                      fill="brand"
                      onClick={() => {
                        archiveLayout()
                      }}
                    />
                  </Tooltip>
                </Flex>
              </Flex>
              {isAdmin && (
                <>
                  <Flex justify="flex-start">
                    <Tooltip
                      text={t(
                        'editor.menus.assets.savedLayouts.changeLayoutLanguage',
                      )}
                      margin="0 12px 0 0"
                    >
                      <Dropdown
                        selected={[layoutLanguage, setLayoutLanguage]}
                        placeholder={t(
                          'editor.menus.assets.savedLayouts.selectLanguage',
                        )}
                        textKey="label"
                        valueKey="value"
                        label={t(
                          'editor.menus.assets.savedLayouts.languageLabel',
                        )}
                        items={LOCALE_OPTIONS.map((opt) => ({
                          ...opt,
                          label: t(`editor.language.${opt.label}`),
                        }))}
                        width="200px"
                        disabled={!showSavedLayoutControls}
                      />
                    </Tooltip>
                    <Tooltip
                      text={t(
                        'editor.menus.assets.savedLayouts.makeGlobalLayout',
                      )}
                      margin="0 12px 0 0"
                    >
                      <ToggleSwitch
                        label={t(
                          'editor.menus.assets.savedLayouts.isGlobalTemplate',
                        )}
                        labelRight
                        value={[isGlobalTemplate, setIsGlobalTemplate]}
                        margin="0 16px"
                        disabled={!showSavedLayoutControls}
                      />
                    </Tooltip>
                  </Flex>
                </>
              )}
              <Flex justify="space-between">
                <Button
                  data-testid="saved-layout-update-button"
                  disabled={!showSavedLayoutControls || isSaving}
                  onClick={updateLayout}
                >
                  {t('editor.menus.assets.savedLayouts.saveButton')}
                </Button>
              </Flex>
            </Flex>
          )}
        </Flex>
      )}
    </Flex>
  )
}

export default SelectLayoutDrawer
